import type { JSX, KeyboardEvent } from 'react';

/**
 * Die Teilnehmer gleich beim Anlegen (BUC-80, BUC-127; 19.09.2026).
 *
 * „Bei der Anlage der Buchung sollen auch Teilnehmer angelegt werden können,
 * bzw. die Auswahlmöglichkeit, dass Anmelder gleich Teilnehmer ist."
 *
 * BUC-127: „Der Anmelder kann zugleich Teilnehmer sein oder ausschließlich
 * bestellen. Beides ist ohne doppelte Datenerfassung möglich." Der Haken
 * übernimmt Name, E-Mail und Geburtsdatum des Anmelders in Platz 1 — getippt
 * wird nichts ein zweites Mal, und geändert wird es am Kunden, nicht hier.
 *
 * LEERE ZEILEN SIND ERLAUBT. Ein Platz ohne Namen bleibt offen und kommt in
 * die Arbeitsliste (BUC-70); die Frist für die Meldung steht darunter. Wer
 * am Telefon zehn Plätze für eine Firma bucht, kennt die Namen selten schon.
 */

/** So viele Zeilen stehen im Formular; der Rest wird nachgereicht. */
export const ERFASSUNG_HOECHSTENS = 30;

export type Teilnehmereingabe = {
  readonly name: string;
  readonly email: string;
  readonly geburtsdatum: string;
};

export type Anmelder = {
  readonly name: string;
  readonly email: string | null;
  readonly geburtsdatum: string | null;
};

const LEER: Teilnehmereingabe = { name: '', email: '', geburtsdatum: '' };

/*
 * Eine Adresse, die der Server annimmt (zod `.email()`). Sie wird HIER
 * geprueft, weil eine unsaubere Adresse in Platz 7 sonst die ganze Buchung
 * scheitern liesse — mit einer Fehlermeldung zu einem Feld, das der Anwender
 * vor sich hat (ERR-03).
 */
const EMAILMUSTER = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function mailFalsch(wert: string): boolean {
  const w = wert.trim();
  return w !== '' && !EMAILMUSTER.test(w);
}

/** Die Plaetze mit unlesbarer E-Mail — die Maske sperrt das Buchen danach. */
export function unlesbareMails(
  plaetze: number,
  zeilen: Readonly<Record<number, Teilnehmereingabe>>,
): readonly number[] {
  const bis = Math.min(Number.isFinite(plaetze) ? plaetze : 0, ERFASSUNG_HOECHSTENS);
  const offen: number[] = [];
  for (let position = 1; position <= bis; position += 1) {
    if (mailFalsch((zeilen[position] ?? LEER).email)) offen.push(position);
  }
  return offen;
}

/** Was an den Server geht: nur Zeilen mit Namen, nur innerhalb der Plätze. */
export function teilnehmerFuerAnlage(
  plaetze: number,
  anmelder: Anmelder | null,
  anmelderDabei: boolean,
  zeilen: Readonly<Record<number, Teilnehmereingabe>>,
): { position: number; name: string; email?: string; geburtsdatum?: string }[] {
  const ergebnis: { position: number; name: string; email?: string; geburtsdatum?: string }[] = [];
  const bis = Math.min(plaetze, ERFASSUNG_HOECHSTENS);
  for (let position = 1; position <= bis; position += 1) {
    const z: Teilnehmereingabe =
      position === 1 && anmelderDabei && anmelder !== null
        ? { name: anmelder.name, email: anmelder.email ?? '', geburtsdatum: anmelder.geburtsdatum ?? '' }
        : (zeilen[position] ?? LEER);
    if (z.name.trim() === '') continue;
    ergebnis.push({
      position,
      name: z.name.trim(),
      ...(z.email.trim() === '' ? {} : { email: z.email.trim() }),
      ...(z.geburtsdatum === '' ? {} : { geburtsdatum: z.geburtsdatum }),
    });
  }
  return ergebnis;
}

export function Teilnehmererfassung({
  plaetze,
  anmelder,
  anmelderDabei,
  beiAnmelderDabei,
  zeilen,
  beiAenderung,
}: {
  readonly plaetze: number;
  /** `null`, solange kein Kunde (oder beim Geschäftskunden kein Anmelder) gewählt ist. */
  readonly anmelder: Anmelder | null;
  readonly anmelderDabei: boolean;
  readonly beiAnmelderDabei: (dabei: boolean) => void;
  readonly zeilen: Readonly<Record<number, Teilnehmereingabe>>;
  readonly beiAenderung: (position: number, zeile: Teilnehmereingabe) => void;
}): JSX.Element {
  const anzahl = Math.max(0, Math.min(Number.isFinite(plaetze) ? plaetze : 0, ERFASSUNG_HOECHSTENS));
  const positionen = Array.from({ length: anzahl }, (_, i) => i + 1);

  /*
   * DIE EINGABETASTE BUCHT NICHT (19.09.2026).
   *
   * Ein Formular sendet bei Enter in einem Textfeld ab — das ist die
   * implizite Absendung des Browsers. Gemessen: Enter im Namensfeld loeste
   * das Buchen aus. In einer Tabelle mit bis zu dreissig Zeilen ist Enter
   * aber die Taste, mit der man die Zeile bestaetigt. Sie springt deshalb
   * ins naechste Feld, statt den Vorgang auszuloesen.
   */
  const beiTaste = (ereignis: KeyboardEvent<HTMLFieldSetElement>): void => {
    if (ereignis.key !== 'Enter') return;
    const ziel = ereignis.target as HTMLElement;
    if (ziel.tagName !== 'INPUT') return;
    ereignis.preventDefault();
    const felder = [
      ...(ereignis.currentTarget.querySelectorAll<HTMLInputElement>('tbody input:not([readonly])')),
    ];
    const stelle = felder.indexOf(ziel as HTMLInputElement);
    felder[stelle + 1]?.focus();
  };

  return (
    <fieldset className="feld teilnehmererfassung" onKeyDown={beiTaste}>
      <legend className="feld__beschriftung">Teilnehmer</legend>
      <label className="feld__beschriftung">
        <input
          type="checkbox"
          checked={anmelderDabei && anmelder !== null}
          disabled={anmelder === null || anzahl === 0}
          onChange={(e) => beiAnmelderDabei(e.target.checked)}
        />{' '}
        Anmelder ist Teilnehmer (Platz 1)
        {anmelder !== null && <span className="kanalliste__name"> — {anmelder.name}</span>}
      </label>
      {anmelder === null && (
        <p className="feld__hilfe">
          Erst mit dem Kunden steht fest, wer anmeldet — beim Geschäftskunden der gewählte
          Ansprechpartner.
        </p>
      )}

      {anzahl > 0 && (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle" aria-label="Teilnehmer erfassen">
            <thead>
              <tr>
                <th scope="col">Platz</th>
                <th scope="col">Name</th>
                <th scope="col">E-Mail</th>
                <th scope="col">Geburtsdatum</th>
              </tr>
            </thead>
            <tbody>
              {positionen.map((position) => {
                const vomAnmelder = position === 1 && anmelderDabei && anmelder !== null;
                const z: Teilnehmereingabe = vomAnmelder
                  ? {
                      name: anmelder.name,
                      email: anmelder.email ?? '',
                      geburtsdatum: anmelder.geburtsdatum ?? '',
                    }
                  : (zeilen[position] ?? LEER);
                const setze = (teil: Partial<Teilnehmereingabe>): void =>
                  beiAenderung(position, { ...z, ...teil });
                return (
                  <tr key={position}>
                    <td>{position}</td>
                    <td>
                      <input
                        className="feld__eingabe"
                        aria-label={`Platz ${String(position)}: Name`}
                        value={z.name}
                        readOnly={vomAnmelder}
                        onChange={(e) => setze({ name: e.target.value })}
                      />
                    </td>
                    <td className={mailFalsch(z.email) ? 'feld--fehlerhaft' : undefined}>
                      <input
                        type="email"
                        className="feld__eingabe"
                        aria-label={`Platz ${String(position)}: E-Mail`}
                        aria-invalid={mailFalsch(z.email)}
                        value={z.email}
                        readOnly={vomAnmelder}
                        onChange={(e) => setze({ email: e.target.value })}
                      />
                      {mailFalsch(z.email) && (
                        <small className="feld__fehler">
                          Keine lesbare Adresse — Beispiel: name@firma.de
                        </small>
                      )}
                    </td>
                    <td>
                      <input
                        type="date"
                        className="feld__eingabe"
                        aria-label={`Platz ${String(position)}: Geburtsdatum`}
                        value={z.geburtsdatum}
                        readOnly={vomAnmelder}
                        onChange={(e) => setze({ geburtsdatum: e.target.value })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="feld__hilfe">
        Leere Zeilen bleiben offen und erscheinen in der Arbeitsliste — die Namen können bis zur
        Frist nachgereicht werden.
        {plaetze > ERFASSUNG_HOECHSTENS
          && ` Hier stehen die ersten ${String(ERFASSUNG_HOECHSTENS)} Plätze; die übrigen werden nach dem Buchen gemeldet.`}
      </p>
    </fieldset>
  );
}
